import livestreamActions from '../../actions/LivestreamActions';
import storeManager from '../../store/StoreManager';

// Constants
const WEBCAM_HEALTH_CHECK_TIMEOUT_MS = 10000; // 10 seconds
// According to the IIB's set luminosity threshold which is 0.2 on the scale of 0-1.
// Setting this to 51 to match the average brightness value matching the one already set in IIB
const LUMINOSITY_THRESHOLD = 51; // Minimum average brightness value (0-255)
const ANALYSIS_INTERVAL_MS = 500; // Check every 500ms

/**
 * Analyzes a video frame to determine its average brightness
 * @param {HTMLVideoElement} videoElement - The video element to analyze
 * @returns {number} - The average brightness value (0-255)
 */
function analyzeFrameBrightness(videoElement, canvasElement) {
    const context = canvasElement.getContext('2d');
    context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

    const imageData = context.getImageData(0, 0, canvasElement.width, canvasElement.height);
    const data = imageData.data;

    let totalBrightness = 0;
    const pixelCount = data.length / 4; // 4 values per pixel (RGBA)

    // Return 0 if there are no pixels
    if (pixelCount === 0) {
        return 0;
    }

    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Calculate brightness using luminance formula
        const brightness = (0.299 * r) + (0.587 * g) + (0.114 * b);
        totalBrightness += brightness;
    }

    return totalBrightness / pixelCount;
}

// Store the current health check timer and video element
let videoElement = null;
let canvasElement = null;
let analysisInterval = null;
let healthCheckTimeout = null;
let isHealthCheckActive = false; // Add this flag to track active state

/**
 * Stops any ongoing webcam health check
 */
function stopWebcamHealthCheck() {
    // Set flag to prevent any pending callbacks from executing
    isHealthCheckActive = false;

    // Clear timers
    if (analysisInterval) {
        clearInterval(analysisInterval);
        analysisInterval = null;
    }

    if (healthCheckTimeout) {
        clearTimeout(healthCheckTimeout);
        healthCheckTimeout = null;
    }

    // Clean up video element
    if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
        videoElement.onloadedmetadata = null;
        // Remove from DOM if it was added
        if (videoElement.parentNode) {
            videoElement.parentNode.removeChild(videoElement);
        }
        videoElement = null;
    }

    // Clean up canvas element
    if (canvasElement) {
        // Clear canvas context
        const context = canvasElement.getContext('2d');
        if (context) {
            context.clearRect(0, 0, canvasElement.width, canvasElement.height);
        }
        // Remove from DOM if it was added
        if (canvasElement.parentNode) {
            canvasElement.parentNode.removeChild(canvasElement);
        }
        canvasElement = null;
    }
}

/**
 * Starts monitoring a video stream for health (brightness)
 * @param {MediaStream} stream - The media stream to monitor
 */
function startWebcamHealthCheck(stream) {
    // Set flag to indicate health check is active
    isHealthCheckActive = true;

    // Dispatch initial status
    storeManager.dispatch(
        livestreamActions.setWebcamHealthCheckStatus(
            livestreamActions.webcamHealthCheckStatus.UNDIAGNOSED
        )
    );

    // Create video element if it doesn't exist
    if (!videoElement) {
        videoElement = document.createElement('video');
        videoElement.autoplay = true;
        videoElement.muted = true;
    }

    // Create canvas element if it doesn't exist
    if (!canvasElement) {
        canvasElement = document.createElement('canvas');
    }

    videoElement.srcObject = stream;

    videoElement.onloadedmetadata = () => {
        // Check if health check is still active before proceeding
        if (!isHealthCheckActive || !videoElement) {
            return;
        }

        // Set up canvas dimensions
        canvasElement.width = videoElement.videoWidth;
        canvasElement.height = videoElement.videoHeight;

        // Start analysis interval (only if still active)
        if (isHealthCheckActive) {
            analysisInterval = setInterval(() => {
                // Double-check we're still active before each analysis
                if (!isHealthCheckActive || !videoElement || !canvasElement) {
                    if (analysisInterval) {
                        clearInterval(analysisInterval);
                        analysisInterval = null;
                    }
                    return;
                }

                const brightness = analyzeFrameBrightness(videoElement, canvasElement);
                if (brightness > LUMINOSITY_THRESHOLD) {
                    storeManager.dispatch(
                        livestreamActions.setWebcamHealthCheckStatus(
                            livestreamActions.webcamHealthCheckStatus.PASSED
                        )
                    );
                    stopWebcamHealthCheck();
                }
            }, ANALYSIS_INTERVAL_MS);
        }

        // Set timeout for failed status (only if still active)
        if (isHealthCheckActive) {
            healthCheckTimeout = setTimeout(() => {
                // Check if still active before dispatching failure
                if (isHealthCheckActive) {
                    storeManager.dispatch(
                        livestreamActions.setWebcamHealthCheckStatus(
                            livestreamActions.webcamHealthCheckStatus.FAILED
                        )
                    );
                    stopWebcamHealthCheck();
                }
            }, WEBCAM_HEALTH_CHECK_TIMEOUT_MS);
        }
    };

    videoElement.play();
}

/**
 * Resets the webcam health check and starts a new one with the given stream
 * @param {MediaStream} stream - The media stream to monitor
 */
const webcamHealthChecker = {
    startWebcamHealthCheck,
    stopWebcamHealthCheck,
    resetAndStartWebcamHealthCheck(stream) {
        this.stopWebcamHealthCheck();
        this.startWebcamHealthCheck(stream);
    },
    // Exported for testing
    analyzeFrameBrightness,
};

export default webcamHealthChecker;
